import type React from "react";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";

import type { ContainerStopRole } from "../../types/containerOrders";
import type { RouteStopSummary } from "../../utils/containerOrderRoute";

/**
 * A route drawn as the chain of stops it is.
 *
 * Shared by the two tables that show one — the orders list and the containers
 * panel under it — because they show the same thing and were drifting: both had
 * grown their own "first → +N → last" summary, each with its own idea of what
 * counted as an end, and neither could be read without opening the record it
 * was summarising. One component, one appearance, whichever table you are in.
 *
 * Nothing is truncated. A route half-shown has to be opened to be trusted,
 * which is the thing a route column exists to save; so the chain wraps within
 * whatever width its column gives it and the row grows by a line instead.
 */

/**
 * How each kind of stop is drawn.
 *
 * Weight and colour carry the role, so the chain reads at one size without
 * needing a legend beside it: the loading site is the job the order exists for
 * and leads, the terminals bracket it, and the added stops are the detour and
 * sit back.
 */
const ROLE_CLASS: Record<ContainerStopRole, string> = {
  loadingSite: "font-semibold text-ink",
  terminalPickup: "text-gray-700",
  terminalDropoff: "text-gray-700",
  custom: "text-secondary-500",
};

interface RouteChainProps {
  /** The stops to draw, in driving order and already filtered to the live ones. */
  stops: RouteStopSummary[];
  /** What to show when the route is empty. */
  fallback?: React.ReactNode;
  /** Width constraint for callers whose column is not already fixed. */
  className?: string;
}

export const RouteChain: React.FC<RouteChainProps> = ({
  stops,
  fallback = <span className="text-gray-400">—</span>,
  className = "",
}) => {
  const { t } = useTranslation("containerOrders");

  if (!stops.length) return <>{fallback}</>;

  /** What a stop is *for* — which its name, being a company, rarely says. */
  const kindOf = (stop: RouteStopSummary) =>
    stop.kind ? t(`route.stopKinds.${stop.kind}`) : t(`route.${stop.role}`);

  return (
    // Wrapping on a flex row rather than inside a paragraph: each stop is one
    // atom, so a name never breaks across lines and an arrow is never orphaned
    // at the end of one.
    <div
      className={`flex flex-wrap items-center gap-x-1 gap-y-0.5 leading-snug wrap-break-word ${className}`}
    >
      {stops.map((stop, i) => (
        <Fragment key={stop.key}>
          {i > 0 && (
            <span aria-hidden className="text-gray-300">
              →
            </span>
          )}
          <span className={ROLE_CLASS[stop.role]} title={kindOf(stop)}>
            {/* An added stop that was never named is called by what it is for —
                the same fallback the order form uses. */}
            {stop.label || kindOf(stop)}
          </span>
        </Fragment>
      ))}
    </div>
  );
};

export default RouteChain;
